import { useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Search } from 'lucide-react'

import styles from './MemberMultiSelect.module.scss'

import Input from '@/components/Input'

export interface MemberOption {
  userId: string
  email: string
  firstName: string
  lastName: string
  /** Short status shown next to the email (e.g. "Failed"). */
  tag?: string
}

interface MemberMultiSelectProps {
  members: MemberOption[]
  selectedUserIds: string[]
  onChange: (next: string[]) => void
  /** Caller's userId; pinned at top with "(you)" suffix, always shown checked + disabled. Omit when no caller pinning is wanted. */
  callerUserId?: string
  isDisabled?: boolean
  /** Hard cap on the number of selectable members. Omit for no limit. */
  maxSelectable?: number
  /** Header checkbox that selects or clears every row matching the search. */
  showSelectAll?: boolean
  searchPlaceholder?: string
}

// Must match `.row` height in styles.module.scss — the virtualizer positions
// rows by this fixed measurement.
const ROW_HEIGHT = 45

export default function MemberMultiSelect({
  members,
  selectedUserIds,
  onChange,
  callerUserId,
  isDisabled = false,
  maxSelectable,
  showSelectAll = false,
  searchPlaceholder = 'Search team members',
}: MemberMultiSelectProps) {
  const [query, setQuery] = useState('')

  // Pin caller at top of the list. Always shown; never selectable.
  const orderedMembers = useMemo(() => {
    if (!callerUserId) return members
    const caller = members.find((m) => m.userId === callerUserId)
    const others = members.filter((m) => m.userId !== callerUserId)
    return caller ? [caller, ...others] : others
  }, [members, callerUserId])

  // Live search filter; caller stays pinned regardless of filter.
  const visibleMembers = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return orderedMembers
    return orderedMembers.filter((m) => {
      if (m.userId === callerUserId) return true
      const haystack = `${m.email} ${m.firstName} ${m.lastName}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [orderedMembers, query, callerUserId])

  // O(1) lookup during render and toggle. visibleMembers can be the entire
  // enterprise — avoid the O(visible × selected) product.
  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds])
  const isAtCap = maxSelectable !== undefined && selectedUserIds.length >= maxSelectable

  function toggle(userId: string) {
    if (isDisabled || userId === callerUserId) return
    if (selectedSet.has(userId)) {
      onChange(selectedUserIds.filter((id) => id !== userId))
    } else if (!isAtCap) {
      onChange([...selectedUserIds, userId])
    }
  }

  const selectableVisibleIds = visibleMembers.filter((m) => m.userId !== callerUserId).map((m) => m.userId)
  const visibleSelectedCount = selectableVisibleIds.filter((id) => selectedSet.has(id)).length
  const allVisibleSelected = selectableVisibleIds.length > 0 && visibleSelectedCount === selectableVisibleIds.length

  function toggleAllVisible() {
    if (isDisabled) return
    const visibleSet = new Set(selectableVisibleIds)
    if (allVisibleSelected) {
      onChange(selectedUserIds.filter((id) => !visibleSet.has(id)))
      return
    }
    const additions = selectableVisibleIds.filter((id) => !selectedSet.has(id))
    const room = maxSelectable === undefined ? additions.length : Math.max(0, maxSelectable - selectedUserIds.length)
    onChange([...selectedUserIds, ...additions.slice(0, room)])
  }

  // Virtualize the row list: only the visible window is mounted, so an
  // enterprise-scale member list (tens of thousands) stays responsive.
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: visibleMembers.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <label className={styles.counter}>
          {showSelectAll && (
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = visibleSelectedCount > 0 && !allVisibleSelected
              }}
              onChange={toggleAllVisible}
              disabled={isDisabled || selectableVisibleIds.length === 0}
              aria-label="Select all"
            />
          )}
          {maxSelectable !== undefined
            ? `${selectedUserIds.length}/${maxSelectable} selected`
            : `${selectedUserIds.length} selected`}
        </label>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} size={14} strokeWidth={2} aria-hidden="true" />
          <Input
            type="search"
            value={query}
            onInput={setQuery}
            placeholder={searchPlaceholder}
            isDisabled={isDisabled}
            className={styles.search}
          />
        </div>
      </div>
      <div ref={scrollRef} className={styles.list}>
        {visibleMembers.length === 0 ? (
          <div className={styles.empty}>No members match.</div>
        ) : (
          <div className={styles.viewport} style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const m = visibleMembers[virtualRow.index]
              const isCaller = m.userId === callerUserId
              const isSelected = isCaller || selectedSet.has(m.userId)
              const isCheckboxDisabled = isDisabled || isCaller || (!isSelected && isAtCap)
              return (
                <div
                  key={m.userId}
                  className={styles.row}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                  onClick={() => toggle(m.userId)}
                >
                  <span className={styles.email}>
                    {m.email}
                    {isCaller ? ' (you)' : ''}
                  </span>
                  {m.tag && <span className={styles.tag}>{m.tag}</span>}
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={isSelected}
                    onChange={() => toggle(m.userId)}
                    disabled={isCheckboxDisabled}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={isCaller ? `${m.email} (you, auto-included)` : m.email}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
